import { useState, useEffect } from 'react';
import {
  Smile, HeartPulse, Users, Landmark, PoundSterling, ExternalLink, Search, Info,
} from 'lucide-react';
import { getLocationResearch } from '../api';
import { getApiErrorMessage } from '../utils/apiError';
import SectionHeader from './ui/SectionHeader';
import { Skeleton } from './ui/Skeleton';

const THEME_ICONS = {
  dental: Smile,
  health: HeartPulse,
  elderly: Users,
  culture: Landmark,
  socioeconomic: PoundSterling,
};

function ThemeCard({ theme }) {
  const Icon = THEME_ICONS[theme.id] || Search;
  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-2">
        <div className="p-2 rounded-xl bg-dental-50 text-dental-600 ring-1 ring-dental-100"><Icon className="w-4 h-4" /></div>
        <h3 className="font-semibold text-ink-900">{theme.label}</h3>
      </div>
      {theme.answer ? (
        <p className="text-sm text-ink-700 leading-relaxed">{theme.answer}</p>
      ) : (
        <p className="text-sm text-ink-400">{theme.error ? 'Could not fetch this theme.' : 'No summary available.'}</p>
      )}
      {theme.sources?.length > 0 && (
        <ul className="mt-3 space-y-2">
          {theme.sources.map((s, i) => (
            <li key={i} className="text-xs border-t border-ink-100 pt-2">
              <a href={s.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-medium text-blue-600 hover:text-blue-700">
                <ExternalLink className="w-3 h-3 shrink-0" />
                <span className="truncate">{s.title || s.url}</span>
              </a>
              {s.snippet && <p className="text-ink-500 mt-0.5 line-clamp-2">{s.snippet}</p>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Research is on-demand: it calls the paid Tavily API, so it only runs when the
// user clicks "Run research" — never automatically on page load.
function LocationResearch({ name }) {
  const [state, setState] = useState({ requested: false, loading: false, enabled: true, data: null, error: null });

  // Reset when navigating to another location; the new one waits for a click.
  useEffect(() => {
    setState({ requested: false, loading: false, enabled: true, data: null, error: null });
  }, [name]);

  const runResearch = () => {
    setState({ requested: true, loading: true, enabled: true, data: null, error: null });
    getLocationResearch(name)
      .then((res) => {
        setState({ requested: true, loading: false, enabled: res.data.enabled, data: res.data.data, error: null });
      })
      .catch((err) => {
        setState({ requested: true, loading: false, enabled: true, data: null, error: getApiErrorMessage(err, 'Failed to load research') });
      });
  };

  return (
    <div>
      <SectionHeader
        icon={Search}
        title="Local research"
        subtitle="Live web research: dental, health, elderly, culture & socio-economic — each with sources"
      />

      {!state.requested ? (
        <div className="card bg-ink-50/60 flex items-center justify-between gap-3">
          <p className="text-sm text-ink-600">Local research uses the Tavily API and runs only on request.</p>
          <button type="button" onClick={runResearch} className="btn-primary inline-flex items-center gap-1.5 shrink-0">
            <Search className="w-4 h-4" /> Run research
          </button>
        </div>
      ) : state.loading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-40 w-full" />)}
        </div>
      ) : !state.enabled ? (
        <div className="card bg-ink-50/60 flex items-start gap-2">
          <Info className="w-4 h-4 text-ink-400 mt-0.5 shrink-0" />
          <p className="text-sm text-ink-600">
            Live local research is not configured. Set a <code className="px-1 rounded bg-ink-100">TAVILY_API_KEY</code> environment
            variable to enable dental, health, elderly, culture and socio-economic research for every location.
          </p>
        </div>
      ) : state.error ? (
        <div className="card bg-red-50 ring-red-200"><p className="text-red-700 text-sm">{state.error}</p></div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {state.data?.themes?.map((t) => <ThemeCard key={t.id} theme={t} />)}
          </div>
          {state.data?.generatedAt && (
            <p className="text-xs text-ink-400 mt-2">
              Researched {new Date(state.data.generatedAt).toLocaleString('en-GB')} · cached for 12h · powered by Tavily.
            </p>
          )}
        </>
      )}
    </div>
  );
}

export default LocationResearch;
